import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { readLastActivityAt } from '../constants/sessionTimeout';

/** How often to compare the deployed bundle against the loaded one. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Auto-reload a stale tab only once the user has been idle at least this long. */
const IDLE_RELOAD_MS = 5 * 60 * 1000;

/** Resume state stashed across an update reload (tab/section/client). */
const RESUME_STATE_KEY = 'advicelink.updateReload.resume';
const RESUME_STATE_MAX_AGE_MS = 2 * 60 * 1000;

const UPDATE_TOAST_ID = 'app-update-available';

/**
 * Save where the user was (or was navigating to) so the app can restore it
 * after an update reload.
 */
export function stashResumeState(state) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      RESUME_STATE_KEY,
      JSON.stringify({ ...state, stashedAt: Date.now() })
    );
  } catch {
    // Storage unavailable — reload without resume state.
  }
}

/** Read and clear the stashed resume state. Returns null if missing or stale. */
export function consumeResumeState() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(RESUME_STATE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RESUME_STATE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.tab !== 'string') return null;
    if (!Number.isFinite(parsed.stashedAt) || Date.now() - parsed.stashedAt > RESUME_STATE_MAX_AGE_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Stash resume state (if provided) and reload to pick up the new build. */
export function reloadForUpdate(resumeState) {
  if (resumeState) {
    stashResumeState(resumeState);
  }
  window.location.reload();
}

function findLoadedMainScriptSrc() {
  if (typeof document === 'undefined') return null;
  return Array.from(document.scripts)
    .map((script) => script.src)
    .find((src) => /\/static\/js\/main\.[^/]+\.js(\?|$)/.test(src)) || null;
}

/**
 * True when `manifest` advertises a different `main.<hash>.js` than the one this
 * tab loaded — i.e. a newer build has been deployed.
 */
export function manifestHasNewMain(loadedMainSrc, manifest) {
  if (!loadedMainSrc) return false;
  const latestMain = manifest?.files?.['main.js'];
  if (typeof latestMain !== 'string' || !latestMain) return false;
  return !loadedMainSrc.endsWith(latestMain);
}

/**
 * True when Firebase Hosting is serving a newer build than this tab loaded.
 *
 * Safe to call from anywhere: it is a no-op outside a production build and
 * swallows network errors, so the login path and the poller share one check.
 */
export async function isNewBuildDeployed() {
  if (process.env.NODE_ENV !== 'production') return false;
  const loadedMainSrc = findLoadedMainScriptSrc();
  if (!loadedMainSrc) return false;
  try {
    // A static Hosting file, not an API call — the relative path is correct.
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!res.ok) return false;
    const manifest = await res.json();
    return manifestHasNewMain(loadedMainSrc, manifest);
  } catch {
    // Offline, a transient error, or the SPA rewrite served index.html.
    return false;
  }
}

/**
 * True when the user has not touched the page for a while, so reloading cannot
 * interrupt anything they are part-way through typing.
 *
 * `useIdleSessionTimeout` maintains the timestamp (throttled to 30s writes) and
 * only while signed in. A missing timestamp means "unknown", which must never
 * count as idle — note `readLastActivityAt` reports an absent key as 0, not
 * null, so this guards on any falsy value rather than on null alone.
 */
export function userIsIdle() {
  const last = readLastActivityAt();
  if (!last) return false;
  return Date.now() - last >= IDLE_RELOAD_MS;
}

/**
 * Detects when a newer frontend build has been deployed.
 *
 * CRA writes a content-hashed `main.<hash>.js` path into `asset-manifest.json`
 * on every build, so a changed hash means a new deploy. When one is detected:
 * - shows a persistent toast with a "Refresh now" action, and
 * - auto-reloads straight away if the user is idle ({@link userIsIdle}), which
 *   is what picks up an overnight deploy on a tab left open all night.
 *
 * An actively used tab is never reloaded underneath the user; callers should
 * reload on their next in-app navigation while `updateAvailable` is true (via
 * {@link reloadForUpdate}).
 *
 * `getResumeState` supplies the current location ({ tab, section, clientId })
 * so hook-initiated reloads land the user back where they were.
 */
export function useAppUpdateChecker({ enabled, getResumeState }) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const updateAvailableRef = useRef(false);
  const getResumeStateRef = useRef(getResumeState);
  getResumeStateRef.current = getResumeState;

  useEffect(() => {
    if (!enabled) return undefined;
    if (process.env.NODE_ENV !== 'production') return undefined;
    if (!findLoadedMainScriptSrc()) return undefined;

    let cancelled = false;

    const markUpdateAvailable = () => {
      if (updateAvailableRef.current) return;
      updateAvailableRef.current = true;
      setUpdateAvailable(true);
      toast.info('A new version of the app is available.', {
        id: UPDATE_TOAST_ID,
        duration: Infinity,
        action: {
          label: 'Refresh now',
          onClick: () => reloadForUpdate(getResumeStateRef.current?.()),
        },
      });
    };

    /** Reload now if a new build is waiting and the user is idle. */
    const reloadIfIdle = () => {
      if (!updateAvailableRef.current) return false;
      if (!userIsIdle()) return false;
      reloadForUpdate(getResumeStateRef.current?.());
      return true;
    };

    const checkForUpdate = async () => {
      const isNew = await isNewBuildDeployed();
      if (cancelled || !isNew) return;
      markUpdateAvailable();
      // Covers a machine that woke from sleep with the tab still visible, where
      // no visibility or focus event ever fires.
      reloadIfIdle();
    };

    const onTabActive = () => {
      if (document.visibilityState === 'hidden') return;
      // Already know about an update: reload without waiting for the fetch.
      if (reloadIfIdle()) return;
      checkForUpdate();
    };

    const intervalId = window.setInterval(checkForUpdate, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onTabActive);
    window.addEventListener('focus', onTabActive);
    checkForUpdate();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onTabActive);
      window.removeEventListener('focus', onTabActive);
      toast.dismiss(UPDATE_TOAST_ID);
    };
  }, [enabled]);

  return { updateAvailable };
}
